import React, { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Button, Modal, Space, Typography } from 'antd'
import { SellerAuthProvider, useSellerAuth } from './contexts/SellerAuthContext'
import SellerLayout from './layouts/SellerLayout'
import SellerLoginPage from './pages/SellerLoginPage'
import DashboardPage from './pages/DashboardPage'
import ProductsPage from './pages/ProductsPage'
import InventoryPage from './pages/InventoryPage'
import OrdersPage from './pages/OrdersPage'
import RolesPage from './pages/RolesPage'
import UsersPage from './pages/UsersPage'
import SupportPage from './pages/SupportPage'
import ProfilePage from './pages/ProfilePage'
import SettingsPage from './pages/SettingsPage'
import SellerRegisterPage from './pages/SellerRegisterPage'
import WalletPage from './pages/WalletPage'
import HeatmapPage from './pages/HeatmapPage.tsx'
import SellerLandingPage from './pages/SellerLandingPage'

import { hydrateSellerCurrencyConfig } from './utils/currency'
import {
  consumeQueuedLoginDisclaimer,
  loginDisclaimerContent,
} from '../../src/shared/loginDisclaimer'

const LoadingScreen = () => <div className="seller-loading-screen">Loading seller workspace...</div>

const SellerCurrencyBootstrap = () => {
  useEffect(() => {
    void hydrateSellerCurrencyConfig()
  }, [])

  return null
}

const SellerOperationalRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, needsPlanActivation } = useSellerAuth()

  if (loading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/seller/login" replace />
  }

  if (needsPlanActivation) {
    return <Navigate to="/seller/plan-activation" replace />
  }

  return <>{children}</>
}

const SellerPlanActivationRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, hasOperationalAccess } = useSellerAuth()

  if (loading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/seller/login" replace />
  }

  if (hasOperationalAccess) {
    return <Navigate to="/seller/dashboard" replace />
  }

  return <>{children}</>
}

const SellerPermissionRoute = ({
  requiredPermission,
  children,
}: {
  requiredPermission: string
  children: React.ReactNode
}) => {
  const { hasPermission } = useSellerAuth()

  if (hasPermission(requiredPermission)) {
    return <>{children}</>
  }

  return (
    <div className="seller-loading-screen" style={{ minHeight: 420, textAlign: 'center', display: 'grid', gap: 8, alignContent: 'center' }}>
      <strong style={{ fontSize: 18 }}>Not permitted</strong>
      <span style={{ color: '#64748b' }}>
        Your current role does not include permission for this section.
      </span>
    </div>
  )
}

const SellerLoginDisclaimerGate = () => {
  const { isAuthenticated, loading } = useSellerAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (loading || !isAuthenticated) {
      return
    }

    if (
      location.pathname === '/seller/login' ||
      location.pathname === '/seller/register'
    ) {
      return
    }

    if (consumeQueuedLoginDisclaimer('seller')) {
      setOpen(true)
    }
  }, [isAuthenticated, loading, location.pathname])

  return (
    <Modal
      open={open}
      title={loginDisclaimerContent.title}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key="continue" type="primary" onClick={() => setOpen(false)}>
          {loginDisclaimerContent.confirmLabel}
        </Button>,
      ]}
      destroyOnHidden
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph strong style={{ marginBottom: 0 }}>
          {loginDisclaimerContent.intro}
        </Typography.Paragraph>

        <ul style={{ margin: 0, paddingInlineStart: 18, color: '#334155' }}>
          {loginDisclaimerContent.highlights.map((item) => (
            <li key={item} style={{ marginBottom: 10, lineHeight: 1.65 }}>
              {item}
            </li>
          ))}
        </ul>

        <Typography.Text type="secondary">
          {loginDisclaimerContent.closing}
        </Typography.Text>
      </Space>
    </Modal>
  )
}

const SellerAppContent = () => {
  return (
    <>
      <SellerLoginDisclaimerGate />
      <Routes>
        <Route path="/" element={<SellerLandingPage />} />
        <Route path="/pricing" element={<SellerLandingPage initialSection="pricing" />} />
        <Route path="/seller/login" element={<SellerLoginPage />} />
        <Route path="/seller/register" element={<SellerRegisterPage />} />
        <Route
          path="/seller/plan-activation"
          element={
            <SellerPlanActivationRoute>
              <SettingsPage />
            </SellerPlanActivationRoute>
          }
        />
        <Route
          path="/seller/*"
          element={
            <SellerOperationalRoute>
              <SellerLayout>
                <Routes>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<SellerPermissionRoute requiredPermission="dashboard.read"><DashboardPage /></SellerPermissionRoute>} />
                  <Route path="products" element={<SellerPermissionRoute requiredPermission="products.read"><ProductsPage /></SellerPermissionRoute>} />
                  <Route path="inventory" element={<SellerPermissionRoute requiredPermission="inventory.read"><InventoryPage /></SellerPermissionRoute>} />
                  <Route path="orders" element={<SellerPermissionRoute requiredPermission="orders.read"><OrdersPage /></SellerPermissionRoute>} />
                  <Route path="heatmap" element={<SellerPermissionRoute requiredPermission="orders.heatmap.view"><HeatmapPage /></SellerPermissionRoute>} />
                  <Route path="roles" element={<SellerPermissionRoute requiredPermission="roles.read"><RolesPage /></SellerPermissionRoute>} />
                  <Route path="users" element={<SellerPermissionRoute requiredPermission="users.read"><UsersPage /></SellerPermissionRoute>} />
                  <Route path="support" element={<SellerPermissionRoute requiredPermission="support.read"><SupportPage /></SellerPermissionRoute>} />
                  <Route path="profile" element={<SellerPermissionRoute requiredPermission="profile.read"><ProfilePage /></SellerPermissionRoute>} />
                  <Route path="wallet" element={<SellerPermissionRoute requiredPermission="wallet.read"><WalletPage /></SellerPermissionRoute>} />
                  <Route path="settings" element={<SellerPermissionRoute requiredPermission="settings.read"><SettingsPage /></SellerPermissionRoute>} />
                  <Route path="*" element={<Navigate to="dashboard" replace />} />
                </Routes>
              </SellerLayout>
            </SellerOperationalRoute>
          }
        />
      </Routes>
    </>
  )
}

const App = () => (
  <SellerAuthProvider>
    <SellerCurrencyBootstrap />
    <SellerAppContent />
  </SellerAuthProvider>
)

export default App

